import { useCallback, useRef, type PointerEvent } from 'react';

/**
 * jQuery UI's bare `.draggable()`, as applied to the two `.floating-icon`s that
 * live in `#sizer_popup`:
 *   `header/view/sizer_popup.js:745` — the AHU Sizer icon (`minimize()`)
 *   `header/view/header.js:560-570`  — the Component Ratings icon
 *
 * Both are the MINIMIZED state of a popup, so both must stay clickable: jQuery
 * UI's mouse widget swallows the `click` that follows a real drag and lets a
 * press-without-movement through. That is reproduced here with a slop
 * threshold — a pointerup that never moved past `slop` px IS the click.
 *
 * Neither legacy call persists the position (no `stop`/`drag` handler, nothing
 * written to localStorage), so neither does this: the icon returns to its CSS
 * anchor whenever the popup changes state. `.floating-icon` is positioned with
 * `right`/`top` (`_sizer.content.scss:348-405`) while jQuery UI writes
 * `left`/`top`, hence the `right: auto` on the first move.
 */

export interface FloatingIconDragOptions {
  /** run on a pointerup that never crossed the slop threshold. */
  onClick: () => void;
  /** px of movement before the gesture becomes a drag instead of a click. */
  slop?: number;
}

export const useFloatingIconDrag = <T extends HTMLElement>({
  onClick,
  slop = 3,
}: FloatingIconDragOptions) => {
  const ref = useRef<T | null>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseLeft: number;
    baseTop: number;
    moved: boolean;
  } | null>(null);

  const onPointerDown = useCallback((e: PointerEvent<T>) => {
    const el = ref.current;
    if (!el || e.button !== 0) return;
    const rect = el.getBoundingClientRect();
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseLeft: rect.left,
      baseTop: rect.top,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: PointerEvent<T>) => {
      const d = drag.current;
      const el = ref.current;
      if (!d || !el || e.pointerId !== d.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved && Math.abs(dx) < slop && Math.abs(dy) < slop) return;
      if (!d.moved) {
        d.moved = true;
        // the base CSS positions with `right`; jQuery UI draggable writes left/top.
        el.style.right = 'auto';
        el.classList.add('ui-draggable-dragging');
      }
      el.style.left = `${d.baseLeft + dx}px`;
      el.style.top = `${d.baseTop + dy}px`;
    },
    [slop]
  );

  const onPointerUp = useCallback(
    (e: PointerEvent<T>) => {
      const d = drag.current;
      const el = ref.current;
      drag.current = null;
      if (!d || !el) return;
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      el.classList.remove('ui-draggable-dragging');
      if (!d.moved) onClick();
    },
    [onClick]
  );

  return {
    ref,
    /** spread onto the icon; `ui-draggable ui-draggable-handle` belongs on its className. */
    dragProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
};

export default useFloatingIconDrag;
